import type {
  ProjectFormData,
  ProjectMappingType,
} from '../../../interfaces/ui/addProjectModal';
import type { Option } from '../../../interfaces/ui/addProjectModal';
import type {
  AdoTestSuite,
  JiraTestPlan,
  JiraTestSuite,
  TestPlan,
  TestSuite,
} from '../../../interfaces/ui/testPlan';

const sortBySequenceWithFallback = <T extends { sequence?: number }>(
  items: T[],
): T[] => {
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const aHasSequence = typeof a.item.sequence === 'number';
      const bHasSequence = typeof b.item.sequence === 'number';

      if (aHasSequence && bHasSequence) {
        const sequenceDiff =
          (a.item.sequence as number) - (b.item.sequence as number);
        return sequenceDiff !== 0 ? sequenceDiff : a.index - b.index;
      }

      if (aHasSequence) return -1;
      if (bHasSequence) return 1;
      return a.index - b.index;
    })
    .map(entry => entry.item);
};

const getJiraSuiteOptions = (
  selectedTestPlan: JiraTestPlan | undefined,
): Option[] => {
  if (!selectedTestPlan?.children?.length) return [];

  const options: Option[] = [];

  const appendSuites = (
    suites: JiraTestSuite[],
    depth: number,
    parentOptionValue?: string,
  ) => {
    sortBySequenceWithFallback(suites).forEach((suite: JiraTestSuite) => {
      const optionValue = suite.id.toString();
      options.push({
        label: suite.name,
        value: optionValue,
        depth,
        parentOptionValue,
      });

      if (suite.children?.length) {
        appendSuites(suite.children, depth + 1, optionValue);
      }
    });
  };

  // Excludes selected test plan root and includes all descendants recursively.
  appendSuites(selectedTestPlan.children, 0);

  return options;
};

const getAdoSuiteOptions = (testSuites: TestSuite[]): Option[] => {
  if (!testSuites.length) return [];

  const suites = testSuites as AdoTestSuite[];
  const suiteById = new Map<string, AdoTestSuite>();
  const childSuitesByParent = new Map<string, AdoTestSuite[]>();
  const rootSuites: AdoTestSuite[] = [];

  suites.forEach((suite: AdoTestSuite) => {
    suiteById.set(suite.id.toString(), suite);
  });

  suites.forEach((suite: AdoTestSuite) => {
    const parentId = suite.parentSuiteId?.toString();
    if (parentId && suiteById.has(parentId)) {
      const siblings = childSuitesByParent.get(parentId) || [];
      siblings.push(suite);
      childSuitesByParent.set(parentId, siblings);
      return;
    }

    rootSuites.push(suite);
  });

  const options: Option[] = [];
  const visited = new Set<string>();

  const appendSuite = (
    suite: AdoTestSuite,
    depth: number,
    parentOptionValue?: string,
    path: Set<string> = new Set(),
  ) => {
    const currentId = suite.id.toString();
    if (path.has(currentId) || visited.has(currentId)) return;

    path.add(currentId);
    visited.add(currentId);

    options.push({
      label: suite.name,
      value: currentId,
      depth,
      parentOptionValue,
    });

    const childSuites = childSuitesByParent.get(currentId) || [];
    childSuites.forEach(childSuite => {
      appendSuite(childSuite, depth + 1, currentId, path);
    });

    path.delete(currentId);
  };

  rootSuites.forEach(rootSuite => appendSuite(rootSuite, 0));

  // Include any disconnected/cyclic suites that were not reached from roots.
  suites.forEach(suite => {
    const suiteId = suite.id.toString();
    if (!visited.has(suiteId)) {
      appendSuite(suite, 0);
    }
  });

  return options;
};

export const getTestSuiteOptions = (
  projectMappingType: ProjectMappingType,
  selectedTestPlanId: string,
  testPlans: TestPlan[],
  testSuites: TestSuite[],
): Option[] => {
  if (!selectedTestPlanId) return [];

  if (projectMappingType === 'JIRA') {
    const selectedTestPlan = testPlans.find(
      (testPlan: TestPlan) => testPlan.id.toString() === selectedTestPlanId,
    ) as JiraTestPlan;

    return getJiraSuiteOptions(selectedTestPlan);
  }

  if (projectMappingType === 'ADO') {
    return getAdoSuiteOptions(testSuites);
  }

  return [];
};

//  ──────────────────────────────────────────────────────
// Form Field Placeholder Text helper functions
//  ──────────────────────────────────────────────────────

export function getMappedProjectPlaceHolderText(
  mappingType: ProjectMappingType,
  loading: boolean,
  mappedProjectsCount: number,
): string {
  if (!mappingType) return 'Select ADO or JIRA first';
  if (loading) return 'Loading mapped projects...';
  if (mappedProjectsCount === 0) return 'No mapped projects found';
  return 'Select a mapped project';
}

export const getTestPlanPlaceHolderText = (
  mappedProjectId: string,
  loading: boolean,
  testPlansCount: number,
  isJira: boolean,
): string => {
  if (!mappedProjectId) return 'Select a mapped project first';
  if (loading) return 'Loading test plans...';
  if (testPlansCount === 0)
    return isJira ? 'No folders found' : 'No test plans found';
  return isJira ? 'Select a folder' : 'Select a test plan';
};

export const getTestSuitePlaceHolderText = (
  testPlanId: string,
  loading: boolean,
  testSuitesCount: number,
  isJira: boolean,
): string => {
  if (!testPlanId)
    return isJira ? 'Select a folder first' : 'Select a test plan first';
  if (loading) return 'Loading test suites...';
  if (testSuitesCount === 0)
    return isJira ? 'No sub-folders found' : 'No test suites found';
  return isJira ? 'Select a sub-folder' : 'Select a test suite';
};

export const getFlowPlaceHolderText = (flowsCount: number): string => {
  if (flowsCount === 0) return 'No flows found';
  return 'Select a flow';
};

//  ──────────────────────────────────────────────────────
// Form Dependency values Reset
//  ──────────────────────────────────────────────────────
type ResetFormField =
  | 'projectMappingType'
  | 'mappedProjectName'
  | 'testPlanName'
  | 'testSuiteName'
  | 'flowName'
  | 'existingChecked'
  | 'existingTestPlan';

// Reset dependent form data fields based on the field that changed.

export function getResetDependentFormFields(
  field: ResetFormField,
): Partial<ProjectFormData> {
  switch (field) {
    case 'projectMappingType':
      return {
        ...resetMappedProject,
        ...resetTestPlan,
        ...resetTestSuite,
        ...resetRepositoryContext,
        ...resetExistingTestPlan,
        ...resetExistingTestSuite,
      };

    case 'mappedProjectName':
      return {
        ...resetTestPlan,
        ...resetTestSuite,
        ...resetRepositoryContext,
        ...resetExistingTestPlan,
        ...resetExistingTestSuite,
      };
    case 'testPlanName':
      return {
        ...resetTestSuite,
      };
    case 'existingChecked':
      return {
        ...resetExistingTestPlan,
        ...resetExistingTestSuite,
      };
    case 'existingTestPlan':
      return {
        ...resetExistingTestSuite,
      };
    default:
      return {};
  }
}

const resetMappedProject = {
  mappedProjectId: '',
  mappedProjectName: '',
  mappedProjectOrg: '',
};

const resetTestPlan = {
  testPlanId: '',
  testPlanName: '',
};

const resetTestSuite = {
  testSuiteId: '',
  testSuiteName: '',
};

const resetRepositoryContext = {
  frontendRepositoryName: '',
  frontendRepositoryUrl: '',
  frontendRepositoryBranch: '',
};

const resetExistingTestPlan = {
  existingTestPlanId: '',
  existingTestPlanName: '',
};

const resetExistingTestSuite = {
  existingTestSuiteId: '',
  existingTestSuiteName: '',
};
